/**
 * CommentManager — canvas comments for excalidraw-app.
 *
 * Owns the comment list of the active document (see `commentStorage.ts`),
 * tracks the comment placement/draft state for the canvas overlay, and keeps
 * the list in sync when the active document changes.
 *
 * Comments are anchored to scene coordinates and rendered as pins on the
 * canvas (`CommentOverlay.tsx`); the sidebar tab (`CommentsPanel.tsx`) lists
 * them as threads with replies and resolve state.
 */

import { randomId } from "@excalidraw/common";

import { appJotaiStore, atom } from "../app-jotai";
import { importUsernameFromLocalStorage } from "../data/localStorage";
import { activeDocumentIdAtom } from "../documents/DocumentManager";
import { documentStorage } from "../documents/documentStorage";

import { commentStorage } from "./commentStorage";

import type { CanvasComment } from "./commentStorage";

// ---------------------------------------------------------------------------
// state
// ---------------------------------------------------------------------------

/** comments of the currently active document */
export const commentsAtom = atom<CanvasComment[]>([]);

/** armed while the user picks a canvas position for a new comment */
export const commentPlacementAtom = atom(false);

/** scene position of the comment draft the composer is open for */
export const commentDraftAtom = atom<{ x: number; y: number } | null>(null);

/** comment highlighted in the panel and on the canvas (pin click / locate) */
export const highlightedCommentIdAtom = atom<string | null>(null);

// ---------------------------------------------------------------------------
// persistence
// ---------------------------------------------------------------------------

/** Updates the atom and persists to IndexedDB, always scoped to the document
 * the comments belong to. */
const setComments = (documentId: string, comments: CanvasComment[]) => {
  appJotaiStore.set(commentsAtom, comments);
  commentStorage.saveComments(documentId, comments).catch((error) => {
    console.error(error);
  });
};

const getActiveDocumentId = () => appJotaiStore.get(activeDocumentIdAtom);

/** Mutates the comment list of the active document and persists the result. */
const mutateComments = (
  updater: (comments: CanvasComment[]) => CanvasComment[],
) => {
  const documentId = getActiveDocumentId();
  if (!documentId) {
    return;
  }
  setComments(documentId, updater(appJotaiStore.get(commentsAtom)));
};

// ---------------------------------------------------------------------------
// initialization & document switching
// ---------------------------------------------------------------------------

let initialized = false;
let unsubscribeFromDocumentChanges: (() => void) | null = null;
let commentGeneration = 0;

export const resetComments = () => {
  commentGeneration++;
  unsubscribeFromDocumentChanges?.();
  unsubscribeFromDocumentChanges = null;
  initialized = false;
  appJotaiStore.set(commentsAtom, []);
  appJotaiStore.set(commentPlacementAtom, false);
  appJotaiStore.set(commentDraftAtom, null);
  appJotaiStore.set(highlightedCommentIdAtom, null);
};

/**
 * Loads the active document's comments and keeps them in sync on document
 * switches. Called once from App's scene initialization, after
 * `initializeDocuments` (which sets the active document id).
 */
export const initializeComments = async () => {
  if (!initialized) {
    initialized = true;

    unsubscribeFromDocumentChanges = appJotaiStore.sub(
      activeDocumentIdAtom,
      () => {
        const documentId = getActiveDocumentId();
        // reset transient comment UI state on switch
        appJotaiStore.set(commentPlacementAtom, false);
        appJotaiStore.set(commentDraftAtom, null);
        appJotaiStore.set(highlightedCommentIdAtom, null);

        if (documentId) {
          const generation = commentGeneration;
          const scope = documentStorage.getScope();
          commentStorage
            .loadComments(documentId)
            .then((comments) => {
              // guard against the user having switched documents again while
              // the read was in flight
              if (
                generation === commentGeneration &&
                documentStorage.getScope() === scope &&
                getActiveDocumentId() === documentId
              ) {
                appJotaiStore.set(commentsAtom, comments);
              }
            })
            .catch((error) => {
              console.error(error);
            });
        } else {
          appJotaiStore.set(commentsAtom, []);
        }
      },
    );
  }

  const documentId = getActiveDocumentId();
  if (documentId) {
    const generation = commentGeneration;
    const scope = documentStorage.getScope();
    const comments = await commentStorage.loadComments(documentId);
    if (
      generation === commentGeneration &&
      documentStorage.getScope() === scope &&
      getActiveDocumentId() === documentId
    ) {
      appJotaiStore.set(commentsAtom, comments);
    }
  }
};

// ---------------------------------------------------------------------------
// CRUD
// ---------------------------------------------------------------------------

export const getCommentAuthor = (): string =>
  importUsernameFromLocalStorage() || "You";

export const addComment = (opts: {
  x: number;
  y: number;
  message: string;
}): CanvasComment | null => {
  const message = opts.message.trim();
  if (!message || !getActiveDocumentId()) {
    return null;
  }

  const comment: CanvasComment = {
    id: randomId(),
    x: opts.x,
    y: opts.y,
    author: getCommentAuthor(),
    message,
    createdAt: Date.now(),
    resolvedAt: null,
    replies: [],
  };

  mutateComments((comments) => [...comments, comment]);
  return comment;
};

export const editComment = (id: string, message: string) => {
  const trimmed = message.trim();
  if (!trimmed) {
    return;
  }
  mutateComments((comments) =>
    comments.map((comment) =>
      comment.id === id ? { ...comment, message: trimmed } : comment,
    ),
  );
};

export const deleteComment = (id: string) => {
  if (appJotaiStore.get(highlightedCommentIdAtom) === id) {
    appJotaiStore.set(highlightedCommentIdAtom, null);
  }
  mutateComments((comments) => comments.filter((comment) => comment.id !== id));
};

export const toggleCommentResolved = (id: string) => {
  mutateComments((comments) =>
    comments.map((comment) =>
      comment.id === id
        ? {
            ...comment,
            resolvedAt: comment.resolvedAt ? null : Date.now(),
          }
        : comment,
    ),
  );
};

export const addReply = (commentId: string, message: string) => {
  const trimmed = message.trim();
  if (!trimmed) {
    return;
  }
  mutateComments((comments) =>
    comments.map((comment) =>
      comment.id === commentId
        ? {
            ...comment,
            replies: [
              ...comment.replies,
              {
                id: randomId(),
                author: getCommentAuthor(),
                message: trimmed,
                createdAt: Date.now(),
              },
            ],
          }
        : comment,
    ),
  );
};

export const deleteReply = (commentId: string, replyId: string) => {
  mutateComments((comments) =>
    comments.map((comment) =>
      comment.id === commentId
        ? {
            ...comment,
            replies: comment.replies.filter((reply) => reply.id !== replyId),
          }
        : comment,
    ),
  );
};
